/* eslint-disable react/destructuring-assignment */
import * as React from 'react';
import {
  TextInput,
  Button,
  StackLayout,
  Tooltip,
  VisuallyHidden,
  Icon,
} from '@leagueplatform/genesis-core';
import type { AnnotatedJsonSchema } from '@web-config-app/core';
import { useIntl } from '@leagueplatform/locales';
import { generateId } from '@web-config-app/core';
import type {
  ControlComponent,
  ControlProps,
} from '@web-config-app/entity-form';
import { EntityFormControl } from '@web-config-app/entity-form';
import { LockedIcon, UnlockedIcon } from './icons';

const getGenerateIdOptions = (schema: AnnotatedJsonSchema) =>
  schema['x-entity-generate']?.options?.id ?? {};

export const GeneratedIdControl: ControlComponent = (props: ControlProps) => {
  const { formatMessage } = useIntl();
  const [isLocked, setIsLocked] = React.useState<boolean>(true);

  const generate = React.useCallback(() => {
    const options = getGenerateIdOptions(props.schema as AnnotatedJsonSchema);
    if (options) {
      return generateId({
        size: options.size,
        alphabet: options.alphabet,
      });
    }
    return null;
  }, [props.schema]);

  return (
    <EntityFormControl
      {...props}
      renderControl={({
        data,
        handleChange,
        path,
        schema,
        required,
        inputStatus,
        enabled,
      }) => {
        const { pattern, maxLength, minLength } = schema;

        return (
          <StackLayout
            orientation="horizontal"
            spacing="$half"
            css={{ width: '100%', flexGrow: 1 }}
            verticalAlignment="center"
          >
            <TextInput
              value={data ?? ''}
              readOnly={!enabled}
              required={required}
              pattern={pattern}
              minLength={minLength}
              maxLength={maxLength}
              type="text"
              inputStatus={inputStatus}
              trailingContent={
                <Tooltip
                  align="center"
                  avoidCollisions
                  content={formatMessage({ id: 'REGENERATE_ID_TOOLTIP' })}
                  side="top"
                >
                  <Button
                    icon="interfaceRefresh"
                    onClick={() => {
                      const newId = generate();
                      if (newId) {
                        handleChange(path, newId);
                        setIsLocked(true);
                      }
                    }}
                    disabled={isLocked}
                    quiet
                    hideLabel
                    priority="secondary"
                    size="medium"
                  >
                    {formatMessage({ id: 'REGENERATE_ID' })}
                  </Button>
                </Tooltip>
              }
              css={{
                backgroundColor: '$inputBackgroundDefault',
                flexGrow: 1,
              }}
            />
            <Tooltip
              align="center"
              avoidCollisions
              content={
                isLocked
                  ? formatMessage({ id: 'UNLOCK_ID_GENERATION_TOOLTIP' })
                  : formatMessage({ id: 'LOCK_ID_GENERATION_TOOLTIP' })
              }
              side="top"
            >
              <Button
                onClick={() => setIsLocked(!isLocked)}
                size="small"
                priority="secondary"
                quiet
              >
                <Icon
                  size={24}
                  icon={isLocked ? <LockedIcon /> : <UnlockedIcon />}
                />
                <VisuallyHidden>
                  {isLocked
                    ? formatMessage({ id: 'UNLOCK_ID_GENERATION' })
                    : formatMessage({ id: 'LOCK_ID_GENERATION' })}
                </VisuallyHidden>
              </Button>
            </Tooltip>
          </StackLayout>
        );
      }}
    />
  );
};
